// src/components/Checkout.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================
// ALL STYLES OUTSIDE THE COMPONENT — never re-injected on render
// ============================================================
const CHECKOUT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .checkout-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 20px; margin-top: 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  @media (max-width: 768px) { .checkout-page { margin-top: 60px; padding: 12px; } }
  @media (max-width: 480px) { .checkout-page { margin-top: 50px; padding: 8px; } }
  .checkout-container { max-width: 1400px; margin: 0 auto; width: 100%; }
  .checkout-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; flex-wrap: wrap; }
  @media (max-width: 480px) { .checkout-header { flex-direction: column; align-items: flex-start; gap: 0.5rem; } }
  .back-button {
    padding: 0.75rem 1.25rem; background: #f8fafc; color: #64748b;
    border: 1px solid #e2e8f0; border-radius: 10px; font-weight: 600;
    cursor: pointer; transition: all 0.3s; white-space: nowrap;
  }
  @media (max-width: 480px) { .back-button { width: 100%; text-align: center; } }
  .back-button:hover { background: #e2e8f0; }
  .checkout-title {
    font-family: 'Playfair Display', serif; font-size: 2.5rem;
    background: linear-gradient(135deg, #334155, #e63946);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    margin: 0; flex: 1;
  }
  @media (max-width: 768px) { .checkout-title { font-size: 2rem; } }
  @media (max-width: 480px) { .checkout-title { font-size: 1.75rem; width: 100%; text-align: center; } }
  .checkout-layout { display: flex; gap: 2rem; align-items: flex-start; }
  @media (max-width: 768px) { .checkout-layout { flex-direction: column; gap: 1.25rem; } }
  .form-section {
    background: white; padding: 2rem; border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08); flex: 1; width: 100%;
    position: relative; z-index: 2;   /* above order-summary z-index:1 */
    min-width: 0;
  }
  @media (max-width: 768px) { .form-section { padding: 1.5rem; } }
  @media (max-width: 480px) { .form-section { padding: 1rem; } }
  .order-summary {
    background: white; padding: 2rem; border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08); width: 400px;
    position: sticky; top: 1.5rem;
    z-index: 1;   /* lower than coupon-section z-index:10 */
    align-self: flex-start;
  }
  @media (max-width: 992px) { .order-summary { width: 350px; padding: 1.5rem; } }
  @media (max-width: 768px) { .order-summary { width: 100%; position: static; padding: 1.25rem; z-index: auto; } }
  @media (max-width: 480px) { .order-summary { padding: 1rem; } }
  .summary-title { font-family: 'Playfair Display', serif; font-size: 1.5rem; color: #334155; margin-bottom: 1.5rem; }
  .form-group { margin-bottom: 1.25rem; }
  .form-label { display: block; margin-bottom: 0.5rem; font-weight: 600; color: #374151; }
  @media (max-width: 480px) { .form-label { font-size: 0.9rem; } }
  .form-input, .form-textarea {
    width: 100%; padding: 0.875rem; border: 1px solid #e2e8f0;
    border-radius: 10px; font-size: 0.95rem; transition: all 0.3s;
    box-sizing: border-box; font-family: inherit;
  }
  @media (max-width: 480px) { .form-input, .form-textarea { padding: 0.75rem; font-size: 0.9rem; } }
  .form-input:focus, .form-textarea:focus {
    outline: none; border-color: #e63946; box-shadow: 0 0 0 3px rgba(230,57,70,0.1);
  }
  .form-input.error { border-color: #ef4444; background: #fef2f2; }
  .form-error { color: #ef4444; font-size: 0.8rem; margin-top: 0.25rem; }
  .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
  @media (max-width: 640px) { .form-grid { grid-template-columns: 1fr; gap: 0.75rem; } }
  /* Phone */
  .phone-input-wrapper { width: 100%; }
  .phone-input-container {
    display: flex; border: 1px solid #e2e8f0; border-radius: 10px;
    overflow: hidden; transition: all 0.3s ease; background: white;
  }
  @media (max-width: 480px) { .phone-input-container { flex-direction: column; border-radius: 8px; } }
  .phone-input-container:focus-within { border-color: #e63946; box-shadow: 0 0 0 3px rgba(230,57,70,0.1); }
  .phone-input-container.has-error { border-color: #ef4444; background: #fef2f2; }
  .country-selector {
    padding: 0.875rem 2rem 0.875rem 0.875rem;
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    border: none; border-right: 1px solid #e2e8f0; cursor: pointer;
    min-width: 130px; font-weight: 600; color: #334155; font-size: 0.9rem;
    appearance: none; -webkit-appearance: none; flex-shrink: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 0.5rem center; background-size: 14px;
    font-family: inherit;
  }
  @media (max-width: 480px) { .country-selector { width: 100%; border-right: none; border-bottom: 1px solid #e2e8f0; min-width: 100%; padding: 0.75rem; } }
  .country-selector:focus { outline: none; }
  .phone-number-field { flex: 1; display: flex; align-items: center; background: white; min-width: 0; }
  .dial-code-prefix {
    padding: 0.875rem 0.5rem 0.875rem 0.875rem; color: #64748b; font-weight: 600;
    border-right: 1px solid #e2e8f0; background: white; white-space: nowrap; flex-shrink: 0; font-size: 0.9rem;
  }
  @media (max-width: 480px) { .dial-code-prefix { padding: 0.75rem 0.5rem; } }
  .phone-input {
    flex: 1; padding: 0.875rem 1rem; border: none !important; font-size: 0.95rem;
    width: 100%; min-width: 0; background: white; outline: none !important;
    box-shadow: none !important; font-family: inherit;
  }
  @media (max-width: 480px) { .phone-input { padding: 0.75rem; } }
  .phone-input:focus { outline: none !important; border: none !important; box-shadow: none !important; }
  .phone-input.error { background: #fef2f2; }
  .phone-input:disabled { background: #f1f5f9; cursor: not-allowed; }
  .phone-error { color: #ef4444; font-size: 0.8rem; margin-top: 0.5rem; padding-left: 0.5rem; }
  /* Payment */
  .payment-options { display: flex; gap: 1rem; margin-bottom: 1.5rem; }
  @media (max-width: 480px) { .payment-options { flex-direction: column; gap: 0.75rem; } }
  .payment-option {
    display: flex; align-items: center; gap: 1rem; padding: 1rem 1.5rem;
    border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer;
    transition: all 0.3s ease; background: white; flex: 1;
    /* CRITICAL: contain the label so it never overlaps elements below it */
    position: relative; z-index: 1;
    overflow: hidden;
  }
  @media (max-width: 480px) { .payment-option { padding: 0.875rem; } }
  .payment-option:hover { border-color: #528FF0; background: #f0f5ff; }
  .payment-option.selected { border-color: #528FF0; background: linear-gradient(135deg, #eef3ff, #f5f8ff); box-shadow: 0 4px 14px rgba(82,143,240,0.18); }
  .payment-radio { accent-color: #528FF0; width: 18px; height: 18px; cursor: pointer; flex-shrink: 0; }
  .payment-info { display: flex; flex-direction: column; }
  .payment-title { font-weight: 700; color: #334155; font-size: 1rem; }
  .payment-description { font-size: 0.8rem; color: #64748b; }
  /* Coupon — explicit stacking so it is ALWAYS above the payment label */
  .coupon-section {
    margin: 1.5rem 0; padding: 1.25rem; background: #f8fafc; border-radius: 12px;
    position: relative; z-index: 10;   /* above payment-option z-index:1 */
  }
  @media (max-width: 480px) { .coupon-section { margin: 1rem 0; padding: 1rem; } }
  .coupon-section h4 { margin-bottom: 1rem; font-size: 1.1rem; color: #334155; }
  .coupon-row { display: flex; gap: 0.75rem; align-items: stretch; }
  @media (max-width: 480px) { .coupon-row { flex-direction: column; gap: 0.5rem; } }
  .coupon-input {
    flex: 1; min-width: 0;
    padding: 0.875rem 1rem; border: 2px solid #e2e8f0;
    border-radius: 8px; font-size: 1rem; font-family: inherit;
    background: white; color: #1e293b; letter-spacing: 1px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    -webkit-appearance: none; appearance: none;
    /* Explicit interactability — never blocked by anything */
    position: relative; z-index: 20;
    pointer-events: all !important;
    cursor: text !important;
    user-select: text !important;
    -webkit-user-select: text !important;
  }
  .coupon-input:focus {
    outline: none !important; border-color: #e63946 !important;
    box-shadow: 0 0 0 3px rgba(230,57,70,0.15) !important;
  }
  .coupon-input:not(:disabled) { cursor: text !important; }
  .coupon-input.error { border-color: #ef4444; background: #fef2f2; }
  .coupon-input.success { border-color: #10b981; background: #f0fdf4; }
  .coupon-input:disabled { background: #f1f5f9; cursor: not-allowed !important; color: #94a3b8; }
  @media (max-width: 480px) { .coupon-input { padding: 0.75rem; font-size: 0.9rem; } }
  .btn-coupon-apply {
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #059669, #10b981);
    color: white; border: none; border-radius: 8px; font-weight: 700;
    cursor: pointer; white-space: nowrap; transition: all 0.25s ease;
    font-family: inherit; font-size: 0.95rem;
  }
  .btn-coupon-apply:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(5,150,105,0.35); }
  .btn-coupon-apply:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
  @media (max-width: 480px) { .btn-coupon-apply { padding: 0.75rem; width: 100%; }  }
  .coupon-msg { margin-top: 0.625rem; font-size: 0.85rem; display: flex; align-items: center; gap: 0.3rem; }
  .coupon-msg.error { color: #dc2626; }
  .coupon-msg.success { color: #059669; font-weight: 600; }
  .applied-coupon { background: linear-gradient(135deg, #f0fdf4, #f7fee7); border: 2px solid #bbf7d0; border-radius: 12px; padding: 1rem; }
  @media (max-width: 480px) { .applied-coupon { padding: 0.875rem; } }
  .applied-coupon-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
  .coupon-badge { display: flex; align-items: center; gap: 0.5rem; background: #dcfce7; padding: 0.5rem 1rem; border-radius: 20px; font-weight: 700; color: #065f46; }
  .btn-remove-coupon { background: none; border: none; cursor: pointer; font-size: 1.1rem; color: #64748b; padding: 0.3rem 0.5rem; border-radius: 4px; transition: all 0.2s; }
  .btn-remove-coupon:hover { color: #ef4444; background: #fee2e2; }
  .coupon-savings { font-size: 0.85rem; color: #374151; }
  /* Pay button */
  .btn-pay {
    width: 100%; padding: 1rem 1.25rem; border: none; border-radius: 10px;
    font-weight: 700; font-size: 1rem; cursor: pointer; transition: all 0.3s;
    display: flex; align-items: center; justify-content: center; gap: 0.75rem;
    background: linear-gradient(135deg, #528FF0, #2869e6); color: white;
    letter-spacing: 0.3px; box-shadow: 0 4px 15px rgba(82,143,240,0.35);
    font-family: inherit;
  }
  @media (max-width: 480px) { .btn-pay { padding: 0.875rem; font-size: 0.95rem; } }
  .btn-pay:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(82,143,240,0.45); }
  .btn-pay:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
  .btn-pay-logo { display: flex; align-items: center; background: white; border-radius: 4px; padding: 2px 6px; }
  .secure-note { text-align: center; margin-top: 0.75rem; font-size: 0.78rem; color: #94a3b8; display: flex; align-items: center; justify-content: center; gap: 0.4rem; flex-wrap: wrap; }
  /* Summary */
  .summary-item { display: flex; justify-content: space-between; padding: 0.75rem 0; border-bottom: 1px solid #f1f5f9; font-size: 0.9rem; }
  .summary-item.total-row { font-size: 1.15rem; font-weight: 700; color: #e63946; padding-top: 1rem; border-top: 2px solid #e2e8f0; border-bottom: none; margin-top: 0.5rem; }
  .summary-item.savings-row { color: #10b981; }
  .summary-item.coupon-row { color: #059669; font-weight: 600; }
  .gst-note { font-size: 0.78rem; color: #94a3b8; padding: 0.25rem 0 0.5rem; }
  .price-breakdown { background: #f8fafc; padding: 0.875rem; border-radius: 8px; margin: 0.75rem 0; font-size: 0.85rem; width: 100%; }
  .price-line { display: flex; justify-content: space-between; padding: 0.2rem 0; color: #475569; }
  .price-line.gst { color: #0891b2; font-weight: 500; }
  .price-line.final { font-weight: 700; color: #16a34a; border-top: 1px dashed #cbd5e1; padding-top: 0.4rem; margin-top: 0.2rem; }
  .price-line.saving { color: #10b981; }
  .instant-banner { margin: 1.25rem 0; padding: 0.875rem; background: linear-gradient(135deg, #dbeafe, #eff6ff); border: 1px solid #93c5fd; border-radius: 8px; font-size: 0.85rem; color: #1d4ed8; text-align: center; display: flex; align-items: center; justify-content: center; gap: 0.5rem; font-weight: 600; }
  /* Auth / empty */
  .auth-required, .empty-cart { max-width: 500px; margin: 2rem auto; text-align: center; padding: 3rem; background: white; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  @media (max-width: 480px) { .auth-required, .empty-cart { padding: 2rem 1.5rem; margin: 1rem auto; } }
  .state-icon { width: 80px; height: 80px; background: linear-gradient(135deg, #e63946, #d00000); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1.5rem; font-size: 2rem; }
  .auth-buttons { display: flex; gap: 1rem; justify-content: center; margin-top: 1.5rem; flex-wrap: wrap; }
  .btn { padding: 0.875rem 1.5rem; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.3s; font-size: 0.95rem; font-family: inherit; }
  .btn-primary { background: linear-gradient(135deg, #e63946, #d00000); color: white; }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(230,57,70,0.3); }
  .btn-outline { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
  .btn-outline:hover { background: #e2e8f0; }
  /* Spinner */
  .spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.4); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; display: inline-block; }
  @keyframes spin { to { transform: rotate(360deg); } }
  /* Download Manager */
  .dl-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 1rem; }
  .dl-box { background: white; border-radius: 20px; max-width: 560px; width: 100%; max-height: 90vh; overflow-y: auto; padding: 2rem; }
  @media (max-width: 480px) { .dl-box { padding: 1.5rem; border-radius: 16px; } }
  .dl-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
  .dl-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; padding: 0.25rem 0.5rem; border-radius: 6px; transition: all 0.2s; }
  .dl-close:hover { color: #ef4444; background: #fee2e2; }
  .progress-track { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; margin: 1rem 0; }
  .progress-fill { height: 100%; background: linear-gradient(90deg, #10b981, #059669); transition: width 0.4s ease; }
  .progress-meta { display: flex; justify-content: space-between; color: #64748b; font-size: 0.85rem; margin-bottom: 1rem; }
  .dl-tip { padding: 0.4rem 0; color: #374151; font-size: 0.9rem; }
  .dl-complete { text-align: center; margin-top: 1.5rem; }
  .dl-emoji { font-size: 3rem; margin-bottom: 0.75rem; }
  .dl-success-msg { font-weight: 600; color: #059669; margin-bottom: 1.25rem; }
  .form-buttons { margin-top: 2rem; }
  input, button, textarea, select { font-family: inherit; }
`;

// Inject styles once, globally — never re-run on render
if (typeof document !== 'undefined' && !document.getElementById('checkout-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'checkout-styles';
  styleEl.textContent = CHECKOUT_STYLES;
  document.head.appendChild(styleEl);
}

// ============================================================
// PHONE INPUT — outside component, stable reference
// ============================================================
const PhoneInput = ({ shippingInfo, countryCodes, errors, isProcessing, onPhoneChange, onCountryCodeChange }) => {
  const current = countryCodes.find(c => c.code === shippingInfo.phoneCountry) || countryCodes[0];
  return (
    <div className="phone-input-wrapper">
      <div className={`phone-input-container ${errors.phone ? 'has-error' : ''}`}>
        <select className="country-selector" value={shippingInfo.phoneCountry} onChange={onCountryCodeChange} disabled={isProcessing}>
          {countryCodes.map(c => (
            <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.dialCode})</option>
          ))}
        </select>
        <div className="phone-number-field">
          <span className="dial-code-prefix">{current.dialCode}</span>
          <input
            type="tel" className={`phone-input ${errors.phone ? 'error' : ''}`}
            value={shippingInfo.phone} onChange={onPhoneChange}
            placeholder={`e.g. ${current.example}`} disabled={isProcessing}
            maxLength="15" autoComplete="tel" inputMode="numeric"
          />
        </div>
      </div>
      {errors.phone && <div className="phone-error">{errors.phone}</div>}
      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>Enter without country code</div>
    </div>
  );
};

// ============================================================
// COUPON SECTION — outside component, uses useRef for input
// so it NEVER loses focus regardless of parent re-renders
// ============================================================
const CouponSection = React.memo(({
  appliedCoupon, couponInputValue, couponError, couponSuccess,
  couponLoading, isProcessing, discountAmount, formatPrice,
  onApplyCoupon, onCouponInputChange, onRemoveCoupon
}) => {
  const inputRef = useRef(null);

  // Keep input focused after every render — belt-and-suspenders fix
  const handleChange = useCallback((e) => {
    onCouponInputChange(e);
    // Re-focus after state update
    requestAnimationFrame(() => {
      if (inputRef.current) inputRef.current.focus();
    });
  }, [onCouponInputChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApplyCoupon();
    }
  }, [onApplyCoupon]);

  if (appliedCoupon) {
    return (
      <div className="coupon-section">
        <h4>🎟️ Coupon Applied</h4>
        <div className="applied-coupon">
          <div className="applied-coupon-header">
            <div className="coupon-badge">
              <span>🎫</span>
              <span>{appliedCoupon.code}</span>
              <span style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem' }}>
                {appliedCoupon.discount}% OFF
              </span>
            </div>
            <button className="btn-remove-coupon" onClick={onRemoveCoupon} disabled={couponLoading || isProcessing} title="Remove coupon">✕</button>
          </div>
          <div className="coupon-savings">
            You saved: <strong style={{ color: '#059669' }}>{formatPrice(discountAmount)}</strong>
            {appliedCoupon.max_discount ? ` · Max: ${formatPrice(appliedCoupon.max_discount)}` : ''}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coupon-section">
      <h4>🎟️ Have a Coupon?</h4>
      <div className="coupon-row">
        <input
          ref={inputRef}
          type="text"
          className={`coupon-input${couponError ? ' error' : ''}${couponSuccess ? ' success' : ''}`}
          value={couponInputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter coupon code"
          disabled={couponLoading || isProcessing}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
        <button
          type="button"
          className="btn-coupon-apply"
          onClick={onApplyCoupon}
          disabled={couponLoading || !couponInputValue.trim() || isProcessing}
        >
          {couponLoading ? <><span className="spinner" style={{ marginRight: '6px' }}></span>Applying…</> : 'Apply'}
        </button>
      </div>
      {couponError  && <div className="coupon-msg error">⚠️ {couponError}</div>}
      {couponSuccess && <div className="coupon-msg success">✅ {couponSuccess}</div>}
    </div>
  );
});
CouponSection.displayName = 'CouponSection';

// ============================================================
// DOWNLOAD MANAGER — outside component
// ============================================================
const DownloadManager = React.memo(({ show, progress, onClose, onGoLibrary }) => {
  if (!show) return null;
  return (
    <div className="dl-overlay">
      <div className="dl-box">
        <div className="dl-header">
          <h3>📚 Downloading Your Books</h3>
          <button className="dl-close" onClick={onClose}>×</button>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progress.percentage}%` }} />
        </div>
        <div className="progress-meta">
          <span>{progress.status === 'completed' ? 'All done!' : `Book ${progress.current} of ${progress.total}`}</span>
          <span>{progress.percentage}%</span>
        </div>
        <div className="dl-tip">📥 Books are being saved to your downloads folder.</div>
        <div className="dl-tip">📁 You can always re-download from your Library.</div>
        <div className="dl-tip">⚠️ Please keep this window open until complete.</div>
        {progress.status === 'completed' && (
          <div className="dl-complete">
            <div className="dl-emoji">🎉</div>
            <p className="dl-success-msg">All books downloaded! Redirecting to library…</p>
            <button className="btn btn-primary" onClick={onGoLibrary}>Go to Library</button>
          </div>
        )}
      </div>
    </div>
  );
});
DownloadManager.displayName = 'DownloadManager';

// ============================================================
// COUNTRY CODES — constant, defined outside so it never changes
// ============================================================
const COUNTRY_CODES = [
  { code: 'IN', name: 'India',         dialCode: '+91',  flag: '🇮🇳', format: '10 digits', pattern: '^[6-9]\\d{9}$',   example: '9876543210' },
  { code: 'US', name: 'United States', dialCode: '+1',   flag: '🇺🇸', format: '10 digits', pattern: '^\\d{10}$',       example: '2125551234' },
  { code: 'GB', name: 'United Kingdom',dialCode: '+44',  flag: '🇬🇧', format: '10 digits', pattern: '^\\d{10}$',       example: '7911123456' },
  { code: 'CA', name: 'Canada',        dialCode: '+1',   flag: '🇨🇦', format: '10 digits', pattern: '^\\d{10}$',       example: '4165551234' },
  { code: 'AU', name: 'Australia',     dialCode: '+61',  flag: '🇦🇺', format: '9 digits',  pattern: '^\\d{9}$',        example: '412345678'  },
  { code: 'AE', name: 'UAE',           dialCode: '+971', flag: '🇦🇪', format: '9 digits',  pattern: '^\\d{9}$',        example: '501234567'  },
  { code: 'SA', name: 'Saudi Arabia',  dialCode: '+966', flag: '🇸🇦', format: '9 digits',  pattern: '^\\d{9}$',        example: '501234567'  },
  { code: 'SG', name: 'Singapore',     dialCode: '+65',  flag: '🇸🇬', format: '8 digits',  pattern: '^\\d{8}$',        example: '91234567'   },
  { code: 'MY', name: 'Malaysia',      dialCode: '+60',  flag: '🇲🇾', format: '9-10 digits',pattern: '^\\d{9,10}$',   example: '123456789'  },
  { code: 'TH', name: 'Thailand',      dialCode: '+66',  flag: '🇹🇭', format: '9 digits',  pattern: '^\\d{9}$',        example: '812345678'  },
];

// ============================================================
// MAIN CHECKOUT COMPONENT
// ============================================================
const Checkout = ({
  auth, cartItems = [], currency = 'INR',
  convertPrice = (p) => `₹${p}`, currencyData = {},
  onOrderSuccess, getPrimaryImage
}) => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://shop.neoteeforge.com/neotee-backend';
  const RAZORPAY_KEY  = 'rzp_live_RzOl2N8ZMXkWYo';

  const [shippingInfo, setShippingInfo] = useState({
    fullName: auth?.user?.name || '', email: auth?.user?.email || '',
    phone: '', phoneCountry: 'IN', address: '', city: '', state: '',
    postalCode: '', country: 'India'
  });
  const [paymentMethod, setPaymentMethod]   = useState('razorpay');
  const [isProcessing, setIsProcessing]     = useState(false);
  const [errors, setErrors]                 = useState({});
  const [isMobile, setIsMobile]             = useState(false);
  const [currentStep, setCurrentStep]       = useState(1);
  const [orderNotes, setOrderNotes]         = useState('');
  const [saveShipping, setSaveShipping]     = useState(false);
  const [showDL, setShowDL]                 = useState(false);
  const [dlProgress, setDlProgress]         = useState({ current:0, total:0, percentage:0, status:'pending' });
  const [productDetails, setProductDetails] = useState({});

  // Coupon — all state in parent, CouponSection is pure display
  const [couponInput,   setCouponInput]   = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError,   setCouponError]   = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // ── Effects ────────────────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    const saved = localStorage.getItem('neoteeforge_shipping');
    if (saved) { try { setShippingInfo(p => ({ ...p, ...JSON.parse(saved) })); } catch (_) {} }
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (saveShipping) localStorage.setItem('neoteeforge_shipping', JSON.stringify(shippingInfo));
  }, [shippingInfo, saveShipping]);

  useEffect(() => {
    if (!cartItems.length) return;
    (async () => {
      const details = {};
      for (const item of cartItems) {
        try {
          const r = await fetch(`${API_BASE_URL}/api/products/${item.id}`, {
            headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
          });
          if (r.ok) { const d = await r.json(); details[item.id] = d.product || d; }
          else       { details[item.id] = item; }
        } catch { details[item.id] = item; }
      }
      setProductDetails(details);
    })();
  }, [cartItems, API_BASE_URL, auth?.token]);

  // ── Helpers ────────────────────────────────────────────────
  const getCountry = useCallback((code) =>
    COUNTRY_CODES.find(c => c.code === code) || COUNTRY_CODES[0],
  []);

  const formatPrice = useCallback((price) => {
    if (typeof convertPrice === 'function') return convertPrice(price);
    const sym = currencyData[currency]?.symbol || '₹';
    return `${sym}${Number(price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }, [convertPrice, currency, currencyData]);

  const getEnhanced = useCallback((item) => {
    const d = productDetails[item.id];
    if (!d) return { ...item, mrp: item.price || 0, salePrice: 0, basePrice: item.price || 0, effectiveDiscount: 0, discountedPrice: item.price || 0, gstIncluded: true, gstAmount: 0, finalPrice: item.price || 0, savings: 0, savingsPercentage: 0 };
    const mrp          = parseFloat(d.mrp_price || d.mrp || d.price || item.price || 0);
    const salePrice    = parseFloat(d.sale_price || d.selling_price || 0);
    const defDisc      = parseFloat(d.default_discount || 0);
    const cpnDisc      = parseFloat(d.coupon_discount  || 0);
    const gstIncluded  = d.gst_included === 1 || d.gst_included === true;
    const basePrice    = salePrice > 0 ? salePrice : mrp;
    const effDisc      = Math.max(defDisc, cpnDisc);
    const discountedPrice = basePrice - (basePrice * effDisc / 100);
    let finalPrice = discountedPrice, gstAmount = 0;
    if (!gstIncluded) { gstAmount = discountedPrice * 0.18; finalPrice = discountedPrice + gstAmount; }
    return { ...item, ...d, mrp, salePrice, basePrice, effectiveDiscount: effDisc, discountedPrice, gstIncluded, gstAmount, finalPrice, savings: mrp - finalPrice, savingsPercentage: mrp > 0 ? ((mrp - finalPrice) / mrp * 100).toFixed(2) : 0 };
  }, [productDetails]);

  const computeTotals = useCallback(() => {
    let sub = 0, gst = 0, mrp = 0;
    cartItems.forEach(item => {
      const e = getEnhanced(item); const qty = item.quantity || 1;
      sub += e.finalPrice * qty; gst += e.gstAmount * qty; mrp += e.mrp * qty;
    });
    return { subtotal: sub, gst, mrp, savings: mrp - sub };
  }, [cartItems, getEnhanced]);

  const totals       = computeTotals();
  const discountAmt  = appliedCoupon ? parseFloat(((totals.subtotal * appliedCoupon.discount) / 100).toFixed(2)) : 0;
  const finalTotal   = parseFloat((totals.subtotal - discountAmt).toFixed(2));

  // ── Validation ─────────────────────────────────────────────
  const validatePhone = useCallback((phone, code) => {
    if (!phone.trim()) return 'Phone number is required';
    const c = getCountry(code);
    const clean = phone.replace(/\D/g, '');
    if (!new RegExp(c.pattern).test(clean)) return `Invalid ${c.name} phone (${c.format})`;
    return '';
  }, [getCountry]);

  const validateShipping = useCallback(() => {
    const e = {};
    if (!shippingInfo.fullName.trim()) e.fullName   = 'Full name is required';
    if (!shippingInfo.email.trim())    e.email      = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(shippingInfo.email)) e.email = 'Invalid email';
    const pe = validatePhone(shippingInfo.phone, shippingInfo.phoneCountry);
    if (pe) e.phone = pe;
    if (!shippingInfo.address.trim())    e.address    = 'Address is required';
    if (!shippingInfo.city.trim())       e.city       = 'City is required';
    if (!shippingInfo.state.trim())      e.state      = 'State is required';
    if (!shippingInfo.postalCode.trim()) e.postalCode = 'Postal code is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [shippingInfo, validatePhone]);

  // ── Input handlers ─────────────────────────────────────────
  const handleField = useCallback((field, value) => {
    setShippingInfo(p => ({ ...p, [field]: value }));
    setErrors(p => ({ ...p, [field]: '' }));
  }, []);

  const handlePhone        = useCallback(e => { setShippingInfo(p => ({ ...p, phone: e.target.value }));        setErrors(p => ({ ...p, phone: '' })); }, []);
  const handlePhoneCountry = useCallback(e => { setShippingInfo(p => ({ ...p, phoneCountry: e.target.value })); setErrors(p => ({ ...p, phone: '' })); }, []);

  // ── Coupon handlers — stable, no deps that change on keystroke ──
  const handleCouponChange = useCallback((e) => {
    setCouponInput(e.target.value.toUpperCase());
    setCouponError('');
    setCouponSuccess('');
  }, []); // ← EMPTY DEPS — this function NEVER changes reference

  const handleRemoveCoupon = useCallback(() => {
    setAppliedCoupon(null); setCouponError(''); setCouponSuccess('');
  }, []);

  const handleApplyCoupon = useCallback(async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code)          { setCouponError('Please enter a coupon code'); return; }
    if (appliedCoupon)  { setCouponError('A coupon is already applied'); return; }
    setCouponLoading(true); setCouponError(''); setCouponSuccess('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/products/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: auth?.token ? `Bearer ${auth.token}` : '' },
        body: JSON.stringify({ coupon_code: code, product_ids: cartItems.map(i => i.id) })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Invalid coupon');
      if (data.valid) {
        if (data.min_order_value && totals.subtotal < data.min_order_value) {
          setCouponError(`Min order ${formatPrice(data.min_order_value)} required`); return;
        }
        setAppliedCoupon({ code, discount: data.discount, coupon_id: data.coupon_id, max_discount: data.max_discount });
        setCouponSuccess(`Saved ${formatPrice((totals.subtotal * data.discount) / 100)}!`);
        setCouponInput('');
      } else {
        setCouponError(data.message || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponError(err.message || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  }, [couponInput, appliedCoupon, API_BASE_URL, auth?.token, cartItems, totals.subtotal, formatPrice]);

  // ── Payment ────────────────────────────────────────────────
  const loadRazorpay = useCallback(() => new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  }), []);

  const downloadBook = useCallback(async (bookId, title) => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/books/${bookId}/download`, {
        headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(a); a.click();
      URL.revokeObjectURL(url); document.body.removeChild(a);
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  }, [API_BASE_URL, auth?.token]);

  const triggerDownloads = useCallback(async (bookIds, orderId) => {
    setShowDL(true);
    setDlProgress({ current: 0, total: bookIds.length, percentage: 0, status: 'downloading' });
    for (let i = 0; i < bookIds.length; i++) {
      const book = cartItems.find(x => x.id === bookIds[i]);
      if (!book) continue;
      setDlProgress({ current: i+1, total: bookIds.length, percentage: Math.round(((i+1)/bookIds.length)*100), status: 'downloading' });
      await downloadBook(bookIds[i], book.title || book.name);
      if (i < bookIds.length - 1) await new Promise(r => setTimeout(r, 1000));
    }
    setDlProgress({ current: bookIds.length, total: bookIds.length, percentage: 100, status: 'completed' });
    localStorage.removeItem('neoteeforge_cart');
    setTimeout(() => navigate('/library'), 5000);
  }, [cartItems, downloadBook, navigate]);

  const handleSubmitOrder = useCallback(async (e) => {
    e.preventDefault();
    if (isMobile && currentStep === 1) {
      if (!validateShipping()) { window.scrollTo(0,0); return; }
      setCurrentStep(2); window.scrollTo(0,0); return;
    }
    if (!validateShipping()) { window.scrollTo(0,0); return; }

    setIsProcessing(true);
    try {
      // Compute fresh totals at submit time
      let freshSub = 0, freshGst = 0, freshMrp = 0;
      cartItems.forEach(item => {
        const e = getEnhanced(item); const qty = item.quantity || 1;
        freshSub += e.finalPrice * qty; freshGst += e.gstAmount * qty; freshMrp += e.mrp * qty;
      });
      const freshDiscount = appliedCoupon
        ? parseFloat(((freshSub * appliedCoupon.discount) / 100).toFixed(2)) : 0;
      const freshFinal    = parseFloat((freshSub - freshDiscount).toFixed(2));
      const paise         = Math.round(freshFinal * 100);

      console.log('ORDER SUBMIT → subtotal:', freshSub, '| discount:', freshDiscount, '| final:', freshFinal, '| paise:', paise);

      const country = getCountry(shippingInfo.phoneCountry);
      const items   = cartItems.map(item => {
        const en = getEnhanced(item);
        return {
          id: item.id, name: item.title || item.name, title: item.title || item.name,
          price: en.finalPrice, quantity: parseInt(item.quantity) || 1,
          image: getPrimaryImage ? getPrimaryImage(en) : (item.thumbnail_url || item.image),
          author: item.author, category: item.category || item.category_name,
          pdf_file: en.pdf_file || item.pdf_file, allow_download: true,
          mrp: en.mrp, sale_price: en.salePrice, base_price: en.basePrice,
          discounted_price: en.discountedPrice, final_price: en.finalPrice,
          gst_included: en.gstIncluded, gst_percentage: en.gstIncluded ? 0 : 18,
          gst_amount: en.gstAmount, effective_discount: en.effectiveDiscount
        };
      });

      const orderData = {
        items,
        total:                    freshFinal,
        subtotal:                 parseFloat(freshSub.toFixed(2)),
        subtotal_before_discount: parseFloat(freshSub.toFixed(2)),
        discount_amount:          freshDiscount,
        subtotal_after_discount:  freshFinal,
        gst_amount:               parseFloat(freshGst.toFixed(2)),
        gst_included:             true,
        shipping:                 0,
        tax:                      0,
        razorpay_amount_paise:    paise,
        coupon: appliedCoupon ? { code: appliedCoupon.code, discount: appliedCoupon.discount, coupon_id: appliedCoupon.coupon_id } : null,
        shippingInfo: {
          fullName: shippingInfo.fullName, email: shippingInfo.email,
          phone: `${country.dialCode} ${shippingInfo.phone.replace(/\D/g,'')}`,
          phoneCountry: shippingInfo.phoneCountry, phoneDialCode: country.dialCode,
          address: shippingInfo.address, city: shippingInfo.city, state: shippingInfo.state,
          postalCode: shippingInfo.postalCode, country: shippingInfo.country
        },
        paymentMethod, notes: orderNotes
      };

      // Create backend order
      const backendRes = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: auth?.token ? `Bearer ${auth.token}` : '' },
        body: JSON.stringify(orderData)
      });
      const backendData = await backendRes.json();
      if (!backendData.success) throw new Error(backendData.message || 'Order creation failed');

      const { orderId, orderNumber, razorpayOrderId } = backendData;

      const loaded = await loadRazorpay();
      if (!loaded || !window.Razorpay) throw new Error('Payment gateway unavailable');

      const rzpOptions = {
        key:         RAZORPAY_KEY,
        amount:      paise,
        currency:    'INR',
        name:        'NeoTeeForge Bookstore',
        description: `Order #${orderNumber} – ${cartItems.length} Book(s)`,
        order_id:    razorpayOrderId || undefined,
        prefill:     { name: shippingInfo.fullName, email: shippingInfo.email, contact: `${country.dialCode}${shippingInfo.phone.replace(/\D/g,'')}` },
        theme:       { color: '#528FF0' },
        modal:       { ondismiss: () => { setIsProcessing(false); alert(`Payment cancelled. Order #${orderNumber} saved.`); } },
        notes:       { order_id: orderId, order_number: orderNumber, coupon_code: appliedCoupon?.code || '', discount_amount: freshDiscount, final_amount: freshFinal },
        handler: async (rzpRes) => {
          try {
            const verifyRes = await fetch(`${API_BASE_URL}/api/orders/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: auth?.token ? `Bearer ${auth.token}` : '' },
              body: JSON.stringify({
                razorpay_payment_id: rzpRes.razorpay_payment_id,
                razorpay_order_id:   rzpRes.razorpay_order_id,
                razorpay_signature:  rzpRes.razorpay_signature,
                orderId, coupon_code: appliedCoupon?.code,
                discount_amount: freshDiscount, final_amount: freshFinal
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success && verifyData.verified) {
              if (onOrderSuccess) {
                onOrderSuccess({
                  ...verifyData.order, id: orderId, orderId,
                  paymentStatus: 'paid', status: 'confirmed',
                  razorpayPaymentId: rzpRes.razorpay_payment_id,
                  items: cartItems.map(item => ({ ...item, ...getEnhanced(item) })),
                  coupon: appliedCoupon, discountAmount: freshDiscount,
                  subtotal: freshSub, total: freshFinal
                });
              }
              triggerDownloads(cartItems.map(i => i.id), orderId);
            } else {
              alert('Payment verification failed. Please contact support.');
              setIsProcessing(false);
            }
          } catch (err) {
            alert('Verification error: ' + err.message);
            setIsProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.on('payment.failed', r => { alert(`Payment failed: ${r.error.description}`); setIsProcessing(false); });
      rzp.open();

    } catch (err) {
      console.error('Order error:', err);
      alert('Order failed: ' + err.message);
      setIsProcessing(false);
    }
  }, [
    isMobile, currentStep, validateShipping, cartItems, getEnhanced,
    appliedCoupon, shippingInfo, paymentMethod, orderNotes,
    API_BASE_URL, auth?.token, RAZORPAY_KEY, loadRazorpay,
    onOrderSuccess, triggerDownloads, getPrimaryImage, getCountry
  ]);

  const isAuthenticated = useMemo(() => !!(auth?.user && auth?.token), [auth]);
  const goToCart  = useCallback(() => navigate('/cart'), [navigate]);
  const goToLogin = useCallback(() => navigate('/login', { state: { from: '/checkout' } }), [navigate]);

  // ── Auth / empty guards ────────────────────────────────────
  if (!isAuthenticated) return (
    <div className="checkout-page">
      <div className="auth-required">
        <div className="state-icon">🔒</div>
        <h2>Login Required</h2>
        <p style={{ color: '#64748b', margin: '0.75rem 0 1.5rem' }}>Please log in to complete your purchase.</p>
        <div className="auth-buttons">
          <button className="btn btn-outline" onClick={goToCart}>← Back to Cart</button>
          <button className="btn btn-primary" onClick={goToLogin}>Login to Checkout</button>
        </div>
      </div>
    </div>
  );

  if (!cartItems.length) return (
    <div className="checkout-page">
      <div className="empty-cart">
        <div className="state-icon">🛒</div>
        <h2>Cart is Empty</h2>
        <p style={{ color: '#64748b', margin: '0.75rem 0 1.5rem' }}>Add books before checking out.</p>
        <button className="btn btn-primary" onClick={goToCart}>Browse Books</button>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="checkout-page">
      <DownloadManager
        show={showDL} progress={dlProgress}
        onClose={() => setShowDL(false)}
        onGoLibrary={() => navigate('/library')}
      />

      <div className="checkout-container">
        <div className="checkout-header">
          <button className="back-button" onClick={goToCart} disabled={isProcessing}>← Back to Cart</button>
          <h1 className="checkout-title">Secure Checkout</h1>
        </div>

        <div className="checkout-layout">
          {/* ── FORM ─────────────────────────────────── */}
          <div className="form-section">
            <form onSubmit={handleSubmitOrder} noValidate>

              {/* Shipping */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#1e293b' }}>Shipping Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input type="text" className={`form-input${errors.fullName ? ' error' : ''}`}
                      value={shippingInfo.fullName} onChange={e => handleField('fullName', e.target.value)} disabled={isProcessing} />
                    {errors.fullName && <div className="form-error">{errors.fullName}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <PhoneInput shippingInfo={shippingInfo} countryCodes={COUNTRY_CODES} errors={errors}
                      isProcessing={isProcessing} onPhoneChange={handlePhone} onCountryCodeChange={handlePhoneCountry} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input type="email" className={`form-input${errors.email ? ' error' : ''}`}
                    value={shippingInfo.email} onChange={e => handleField('email', e.target.value)} disabled={isProcessing} />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <textarea className={`form-textarea${errors.address ? ' error' : ''}`}
                    value={shippingInfo.address} onChange={e => handleField('address', e.target.value)}
                    rows="3" disabled={isProcessing} />
                  {errors.address && <div className="form-error">{errors.address}</div>}
                </div>
                <div className="form-grid">
                  {[['city','City'],['state','State'],['postalCode','Postal Code']].map(([field, label]) => (
                    <div className="form-group" key={field}>
                      <label className="form-label">{label} *</label>
                      <input type="text" className={`form-input${errors[field] ? ' error' : ''}`}
                        value={shippingInfo[field]} onChange={e => handleField(field, e.target.value)} disabled={isProcessing} />
                      {errors[field] && <div className="form-error">{errors[field]}</div>}
                    </div>
                  ))}
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginTop:'0.5rem', fontSize:'0.85rem', color:'#475569', cursor:'pointer' }}>
                  <input type="checkbox" checked={saveShipping} onChange={e => setSaveShipping(e.target.checked)} />
                  Save shipping info for next time
                </label>
              </div>

              {/* Payment */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#1e293b' }}>Payment Method</h3>
                <div className="payment-options">
                  <div
                    className={`payment-option${paymentMethod === 'razorpay' ? ' selected' : ''}`}
                    onClick={() => !isProcessing && setPaymentMethod('razorpay')}
                    role="radio"
                    aria-checked={paymentMethod === 'razorpay'}
                    tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !isProcessing && setPaymentMethod('razorpay')}
                  >
                    <input
                      type="radio" className="payment-radio" value="razorpay"
                      checked={paymentMethod === 'razorpay'}
                      onChange={() => setPaymentMethod('razorpay')}
                      disabled={isProcessing}
                      tabIndex={-1}
                      style={{ pointerEvents: 'none' }}
                    />
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect width="28" height="28" rx="6" fill="#528FF0"/>
                      <path d="M17.5 4L10 14.5H15.5L9 24L21 12H15L20.5 4H17.5Z" fill="white"/>
                    </svg>
                    <div className="payment-info">
                      <div className="payment-title" style={{ color: '#528FF0' }}>Razorpay</div>
                      <div className="payment-description">Cards · UPI · Net Banking · Wallets</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coupon — passes stable handlers, no re-mount */}
              <CouponSection
                appliedCoupon={appliedCoupon}
                couponInputValue={couponInput}
                couponError={couponError}
                couponSuccess={couponSuccess}
                couponLoading={couponLoading}
                isProcessing={isProcessing}
                discountAmount={discountAmt}
                formatPrice={formatPrice}
                onApplyCoupon={handleApplyCoupon}
                onCouponInputChange={handleCouponChange}
                onRemoveCoupon={handleRemoveCoupon}
              />

              {/* Pay button */}
              <div className="form-buttons">
                <button type="submit" className="btn-pay" disabled={isProcessing}>
                  {isProcessing ? (
                    <><span className="spinner"></span> Processing…</>
                  ) : (
                    <>
                      <span className="btn-pay-logo">
                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
                          <path d="M17.5 4L10 14.5H15.5L9 24L21 12H15L20.5 4H17.5Z" fill="#528FF0"/>
                        </svg>
                      </span>
                      Pay {formatPrice(finalTotal)} via Razorpay
                    </>
                  )}
                </button>
                <div className="secure-note">🔒 Secured by Razorpay · PCI DSS Compliant</div>
              </div>

            </form>
          </div>

          {/* ── ORDER SUMMARY ──────────────────────────── */}
          <div className="order-summary">
            <h3 className="summary-title">Order Summary</h3>

            {cartItems.map(item => {
              const e = getEnhanced(item); const qty = item.quantity || 1;
              return (
                <div key={item.id} className="summary-item" style={{ flexDirection:'column', alignItems:'flex-start' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', width:'100%', flexWrap:'wrap', gap:'0.25rem' }}>
                    <span><strong>{item.title || item.name}</strong> ×{qty}</span>
                    <span>{formatPrice(e.finalPrice * qty)}</span>
                  </div>
                  <div className="price-breakdown">
                    <div className="price-line"><span>MRP</span><span>{formatPrice(e.mrp * qty)}</span></div>
                    {e.salePrice > 0 && <div className="price-line"><span>Sale price</span><span>{formatPrice(e.salePrice * qty)}</span></div>}
                    {e.effectiveDiscount > 0 && <div className="price-line"><span>Discount ({e.effectiveDiscount}%)</span><span>−{formatPrice((e.basePrice - e.discountedPrice) * qty)}</span></div>}
                    {!e.gstIncluded && <div className="price-line gst"><span>GST 18%</span><span>+{formatPrice(e.gstAmount * qty)}</span></div>}
                    <div className="price-line final"><span><strong>Final</strong></span><span><strong>{formatPrice(e.finalPrice * qty)}</strong></span></div>
                    {e.savings > 0 && <div className="price-line saving"><span>You save</span><span>{formatPrice(e.savings * qty)} ({e.savingsPercentage}%)</span></div>}
                  </div>
                </div>
              );
            })}

            <div style={{ marginTop: '0.5rem' }}>
              <div className="summary-item"><span>Cart total</span><span>{formatPrice(totals.subtotal)}</span></div>
              {totals.gst > 0 && (
                <>
                  <div className="summary-item" style={{ color:'#0891b2' }}><span>GST (in prices)</span><span>{formatPrice(totals.gst)}</span></div>
                  <div className="gst-note">GST is already included in item prices above</div>
                </>
              )}
              {totals.savings > 0 && <div className="summary-item savings-row"><span>Total savings</span><span>−{formatPrice(totals.savings)}</span></div>}
              {appliedCoupon && <div className="summary-item coupon-row"><span>Coupon ({appliedCoupon.code})</span><span>−{formatPrice(discountAmt)}</span></div>}
              <div className="summary-item total-row"><span>You pay</span><span>{formatPrice(finalTotal)}</span></div>
            </div>

            <div className="instant-banner">📥 Instant Digital Download After Purchase</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;